import actionTypes from '../common/actionTypes';
import {
	defaultCustomerPolicy,
	defaultCustomerPolicyBeneficiary,
	defaultTrustee,
	defaultPlanOptions,
	devQuestionnaire,
	defaultDeclineChecker
} from './policyAPI';
import Utils from '../common/utils';
import * as moment from 'moment';

interface State {
	customerPolicy: CustomerPolicy;
	customerPolicyBeneficiary: CustomerPolicyBeneficiaryDto;
	planOptions: CustomerPolicy[];
	questionnaire: object;
	subscription: StripeSubscription;
	deleteSucceed: Boolean;
	policies: CustomerPolicy[];
	beneficiaries: CustomerPolicyBeneficiariesDto;
	trustee: CustomerPolicyBeneficiaryDto;
	ratePlusMinus?: number;
	isHardDeclined: DeclineCheckerDto;
	isSoftDeclined: DeclineCheckerDto;
	// qc hard decline means the ONLY decline reason is selecting Quebec
	qcHardDeclined: boolean;
	questionnaireReport: string;
	answers: PolicyAnswerWithDeclineInfoDto[];
	tempPolicyDocumentUrl: string;
	premiumChanged: boolean;
	redirectFromAdjudication: boolean;
}
const initialState: State = {
	customerPolicy: defaultCustomerPolicy,
	customerPolicyBeneficiary: defaultCustomerPolicyBeneficiary,
	planOptions: defaultPlanOptions,
	questionnaire: {
		heightUnit: 'in',
		weightUnit: 'lbs',
		maxWeightLoss: 0,
		ownPetsTypes: {},
		foreignTravelList: [],
		cancer: {},
		heartCirculatory: {},
		psychological: {},
		reproductive: {},
		head: {},
		immuno: {},
		blood: {},
		liver: {},
		respiratory: {},
		neurological: {},
	},
	subscription: {},
	deleteSucceed: false,
	policies: [],
	beneficiaries: { current: [], pending: [] },
	trustee: defaultTrustee,
	isHardDeclined: defaultDeclineChecker,
	isSoftDeclined: defaultDeclineChecker,
	qcHardDeclined: false,
	questionnaireReport: '',
	answers: [],
	tempPolicyDocumentUrl: '',
	premiumChanged: false,
	redirectFromAdjudication: false,
};
export default function policyReducer(state: State = initialState, action: any) {
	switch (action.type) {
		case actionTypes.SET_DEV_QUESTIONNAIRE:
			return { ...state, questionnaire: devQuestionnaire };
		case actionTypes.RESET_CUSTOMER_POLICY:
			return { ...state, customerPolicy: initialState.customerPolicy };
		case actionTypes.RESET_CUSTOMER_POLICY_BENEFICIARY:
			return { ...state, customerPolicyBeneficiary: initialState.customerPolicyBeneficiary };
		case actionTypes.SET_CUSTOMER_POLICY:	
		{
			// we want to calcuate the dates here.
			let userPolicy: CustomerPolicy = action.customerPolicy;
			if (userPolicy.term && Utils.isValidDate(userPolicy.effectiveDate , 'YYYY-MM-DD')) {
				// adjust the end date. The clone is there to create a new date, w/ the modifications
				// there seems to be a bug in moment, where if you create a date, then adjust 
				// it to an invalid date, checking if it's valid. returns true. However, formatting fails.
				let endDate =  moment(userPolicy.effectiveDate).add(userPolicy.term, 'years').subtract(1, 'day').clone();
				if (endDate.isValid()) {
					userPolicy.endDate = endDate.format('YYYY-MM-DD');
				}
			}

			return { ...state, customerPolicy: userPolicy };
		}	
		case actionTypes.FETCH_CUSTOMER_POLICY:
		case actionTypes.UPDATE_CUSTOMER_POLICY:
		case actionTypes.UPDATE_CUSTOMER_PLAN:
			const policy: CustomerPolicy = action.customerPolicy;
			if (policy.effectiveDate) {
				policy.effectiveDate = Utils.formatDateAsUTC(policy.effectiveDate);
			}
			if (policy.endDate) {
				policy.endDate = Utils.formatDateAsUTC(policy.endDate);
			}

			return { ...state, customerPolicy: policy };
		case actionTypes.FETCH_CUSTOMER_POLICY_FREE_TRIAL_MONTHS:
			const freeTrialMonths = action.months;
			const customerPolicy = { ...state.customerPolicy };
			customerPolicy.freeMonthTrial = freeTrialMonths;
			return { ...state, customerPolicy };
		case actionTypes.FETCH_CUSTOMER_POLICY_DOCUMENT_URL:
			return { ...state, tempPolicyDocumentUrl: action.url };
		case actionTypes.SET_CUSTOMER_POLICIES:	
		case actionTypes.SAVE_CUSTOMER_POLICY:
			let remainingPolicies = state.policies.filter(b => b.id !== action.customerPolicy.id);
			
			const savedPolicy: CustomerPolicy = action.customerPolicy;
			if (savedPolicy.effectiveDate) {
				savedPolicy.effectiveDate = Utils.formatDateAsUTC(savedPolicy.effectiveDate);
			}
			if (savedPolicy.endDate) {
				savedPolicy.endDate = Utils.formatDateAsUTC(savedPolicy.endDate);
			}
			return {
				...state,
				policies: [...remainingPolicies, savedPolicy],
				customerPolicy: savedPolicy
			};
		case actionTypes.FETCH_POLICIES:
		case actionTypes.FETCH_CUSTOMER_POLICIES:
			return { ...state, policies: action.policies };
		case actionTypes.DELETE_CUSTOMER_POLICY:
			let unchangedPolicies = state.policies.filter(b => b.id !== action.customerPolicy.id);
			return {
				...state,
				policies: [...unchangedPolicies, action.customerPolicy],
				customerPolicy: action.customerPolicy
			};
		case actionTypes.FETCH_CUSTOMER_POLICY_BENEFICIARY:
			const beneficiary: CustomerPolicyBeneficiaryDto = action.customerPolicyBeneficiary;
			if (beneficiary.birthDate) {
				beneficiary.birthDate = Utils.formatDateAsUTC(beneficiary.birthDate);
			}

			return { ...state, customerPolicyBeneficiary: beneficiary };
		case actionTypes.SET_TRUSTEE:
			return { ...state, trustee: action.trustee };
		case actionTypes.SET_CUSTOMER_POLICY_BENEFICIARIES:
			return { ...state, beneficiaries: { ...state.beneficiaries, pending: action.beneficiaries } };
		case actionTypes.FETCH_CUSTOMER_POLICY_BENEFICIARIES:
			return { ...state, beneficiaries: action.beneficiaries };

		case actionTypes.SET_QUESTIONNAIRE:
			return { ...state, questionnaire: action.questionnaire };
		case actionTypes.RESET_QUESTIONNAIRE:
			return { ...state, questionnaire: initialState.questionnaire };

		case actionTypes.FETCH_PLAN_OPTIONS:
			return { ...state, planOptions: action.planOptions };
		case actionTypes.SET_CUSTOMER_POLICY_BENEFICIARY:
			if (action.customerPolicyBeneficiary.id) {
				let updatedBeneficiaries = [...state.beneficiaries.pending!];
				const beneficiaryIndex = updatedBeneficiaries.findIndex(b => b.id === action.customerPolicyBeneficiary.id);
				const updatedBeneficiary = { ...state.customerPolicyBeneficiary, ...action.customerPolicyBeneficiary };
				
				if (beneficiaryIndex === -1) {
					updatedBeneficiaries.push(updatedBeneficiary);
				}
				else {
					updatedBeneficiaries.splice(beneficiaryIndex, 1, updatedBeneficiary);
				}

				return {
					...state,
					beneficiaries: { ...state.beneficiaries, pending: updatedBeneficiaries },
					customerPolicyBeneficiary: updatedBeneficiary
				};
			}
			else {
				return { ...state, customerPolicyBeneficiary: action.customerPolicyBeneficiary };
			}
		case actionTypes.SAVE_CUSTOMER_POLICY_BENEFICIARY:
			if (action.customerPolicyBeneficiary.id) {
				let updatedBeneficiaries = [...state.beneficiaries.pending!];
				const beneficiaryIndex = updatedBeneficiaries.findIndex(b => b.id === action.customerPolicyBeneficiary.id);
				const updatedBeneficiary = { ...state.customerPolicyBeneficiary, ...action.customerPolicyBeneficiary };

				if (updatedBeneficiary.birthDate) {
					updatedBeneficiary.birthDate = Utils.formatDateAsUTC(updatedBeneficiary.birthDate);
				}
				
				if (beneficiaryIndex === -1) {
					updatedBeneficiaries.push(updatedBeneficiary);
				}
				else {
					updatedBeneficiaries.splice(beneficiaryIndex, 1, updatedBeneficiary);
				}

				return {
					...state,
					beneficiaries: { ...state.beneficiaries, pending: updatedBeneficiaries },
					customerPolicyBeneficiary: updatedBeneficiary
				};
			}
			else {
				return { ...state, customerPolicyBeneficiary: action.customerPolicyBeneficiary };
			}
		case actionTypes.DELETE_CUSTOMER_POLICY_BENEFICIARY:
			return { ...state, deleteSucceed: action.deleteSucceed };
		case actionTypes.FETCH_RATE_PLUS_MINUS_SUCCESS:
			return { ...state, ratePlusMinus: action.ratePlusMinus };
		case actionTypes.FETCH_IS_HARD_DECLINED_SUCCESS:
			return { ...state, isHardDeclined: action.isHardDeclined };
		case actionTypes.FETCH_IS_SOFT_DECLINED_SUCCESS:
			return { ...state, isSoftDeclined: action.isSoftDeclined };
		case actionTypes.FETCH_QUESTIONNAIRE_REPORT_SUCCESS:
			return { ...state, questionnaireReport: action.questionnaireReport };
		case actionTypes.FETCH_POLICY_ANSWER_SUCCESS:
			return { ...state, answers: action.answers };
		case actionTypes.SET_PREMIUM_CHANGED:
			return { ...state, premiumChanged: action.premiumChanged };
		case actionTypes.RESET_POLICY_ANSWER:
			return { ...state, answers: [] };
		case actionTypes.REDIRECT_FROM_ADJUDICATION:
			return { ...state, redirectFromAdjudication: action.redirectFromAdjudication };
		case actionTypes.UPDATE_POLICY_PAPER_STATUS:
		case actionTypes.SAVE_CUSTOMER_POLICY_CURRENT_BENEFICIARY:
		case actionTypes.DELETE_BENEFICIARY_DURING_SIGN_UP:
		default:
			return state;
	}
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/policy/policyReducer.ts